import { useState, useEffect } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import Cookies from 'js-cookie';
import { Select, DatePicker } from 'antd';
import { getAccessTokenOrRedirect } from './utils/auth';
import UserDropdown from './components/dashboard/UserDropdown';
import { InputsSection } from './components/dashboard/InputsSection';
import { EffortsSection } from './components/dashboard/EffortsSection';
import { ResultsSection } from './components/dashboard/ResultsSection';
import RewardsPreview from './components/dashboard/RewardsPreview';
import { useAuth } from './providers/AuthProvider';
import { API_URL } from './env';
import {
  SalesData, PipelineStage, RevenueData,
  QuotationsData, EnquiriesData, OpportunitiesData,
  SalesPerformanceDataPoint,
} from './components/dashboard/types';
import { usePermission } from './permission/PermissionContext';

dayjs.extend(quarterOfYear);

const { RangePicker } = DatePicker;

const periodOptions: { value: string; label: string; groupBy: 'day' | 'week' | 'month' }[] = [
  { value: 'this-week', label: 'This Week', groupBy: 'day' },
  { value: 'last-week', label: 'Last Week', groupBy: 'day' },
  { value: 'this-month', label: 'This Month', groupBy: 'week' },
  { value: 'last-month', label: 'Last Month', groupBy: 'week' },
  { value: 'this-quarter', label: 'This Quarter', groupBy: 'month' },
  { value: 'last-quarter', label: 'Last Quarter', groupBy: 'month' },
  { value: 'this-year', label: 'This Year', groupBy: 'month' },
  { value: 'last-year', label: 'Last Year', groupBy: 'month' },
  { value: 'custom', label: 'Custom', groupBy: 'day' },
];

interface ContactCompanyStats {
  total_count?: number;
  not_contacted_count?: number;
  not_contacted_in_3months?: number;
}

const createPipelineData = (
  salesData: SalesData,
  revenueData: RevenueData,
  quotationsData: QuotationsData,
  enquiriesData: EnquiriesData,
  opportunitiesData: OpportunitiesData
): PipelineStage[] => [
    { stage: 'Enquiries', count: enquiriesData.total_enquiries },
    { stage: 'Opportunities', count: opportunitiesData.total_opportunities },
    { stage: 'Quotations', count: quotationsData.total_quotations, value: quotationsData.total_amount },
    { stage: 'Sales Orders', count: salesData.total_orders, value: salesData.total_sales },
    { stage: 'Invoices', count: revenueData.total_invoices, value: revenueData.total_revenue }
  ];

const SalesDashboard = () => {
  const { userInfo } = useAuth();
  const fullHierachyAccess = usePermission('full_hierachy_level_access');

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [callsData, setCallsData] = useState<any[]>([]);
  const [emailsData, setEmailsData] = useState<any[]>([]);
  const [contactCompanyStats, setContactCompanyStats] = useState<ContactCompanyStats>({});
  const [callsLoading, setCallsLoading] = useState(true);
  const [emailsLoading, setEmailsLoading] = useState(true);
  const [contactCompanyStatsLoading, setContactCompanyStatsLoading] = useState(true);
  const [callsError, setCallsError] = useState<string | null>(null);
  const [emailsError, setEmailsError] = useState<string | null>(null);
  const [contactCompanyStatsError, setContactCompanyStatsError] = useState<string | null>(null);
  const [salesData, setSalesData] = useState<SalesData>({
    total_sales: 0,
    total_orders: 0,
    monthly_data: [],
    daily_data: [],
    performance_data: [],
  });
  const [salesOrderData, setSalesOrderData] = useState<{
    total_sales: number;
    total_orders: number;
  }>({
    total_sales: 0,
    total_orders: 0
  });
  const [revenueData, setRevenueData] = useState<RevenueData>({
    total_revenue: 0,
    total_invoices: 0
  });
  const [quotationsData, setQuotationsData] = useState<QuotationsData>({
    total_quotations: 0,
    total_amount: 0
  });
  const [enquiriesData, setEnquiriesData] = useState<EnquiriesData>({
    total_enquiries: 0,
    total_items: 0
  });
  const [opportunitiesData, setOpportunitiesData] = useState<OpportunitiesData>({
    total_opportunities: 0,
    total_items: 0
  });
  const [finacePermanceData, setFinacePermanceData] = useState<any[]>([]);

  const [salesLoading, setSalesLoading] = useState(true);
  const [salesOrderDataLoading, setSalesOrderDataLoading] = useState(true);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [quotationsLoading, setQuotationsLoading] = useState(true);
  const [enquiriesLoading, setEnquiriesLoading] = useState(true);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(true);
  const [finacePerformanceLoading, setFinacePerformanceLoading] = useState(true);

  const [salesError, setSalesError] = useState<string | null>(null);
  const [salesOrderDataError, setSalesOrderDataError] = useState<string | null>(null);
  const [revenueError, setRevenueError] = useState<string | null>(null);
  const [quotationsError, setQuotationsError] = useState<string | null>(null);
  const [enquiriesError, setEnquiriesError] = useState<string | null>(null);
  const [opportunitiesError, setOpportunitiesError] = useState<string | null>(null);
  const [finacePerformanceError, setFinacePerformanceError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('this-month');
  const [pickerRange, setPickerRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [dateRange, setDateRange] = useState<{ startDate: string, endDate: string }>({
    startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
  });
  const [isCustomDate, setIsCustomDate] = useState(false);
  const [groupBy, setGroupBy] = useState<'day' | 'month' | 'week'>('week');
  const periodLabel = periodOptions.find(p => p.value === selectedPeriod)?.label || 'This Month';

  // Initial setup effect - runs once to set initial selected user
  useEffect(() => {
    if (!userInfo) return;
    if (fullHierachyAccess === 'loading') return;

    const initializeSelectedUser = async () => {
      try {
        const staffid = userInfo.userId;

        if (!staffid) {
          throw new Error('Staff ID not found');
        }

        if (userInfo.isAdmin || fullHierachyAccess) {
          // For admin users, no user is selected initially
          setSelectedUser(null);
        } else if (userInfo.isTeamLead) {
          // For team leads, set team lead as default selected user
          setSelectedUser(staffid);
        } else {
          // For regular users, set their own staffid as selected user
          setSelectedUser(staffid);
        }
      } catch (error) {
        console.error('Failed to initialize selected user:', error);
      }
    };

    initializeSelectedUser();
  }, [userInfo, fullHierachyAccess]); // Run when userInfo changes

  const calculateDateRange = (period: string): [dayjs.Dayjs, dayjs.Dayjs] => {
    const now = dayjs();
    switch (period) {
      case 'this-week':
        return [now.startOf('week'), now.endOf('week')];
      case 'last-week':
        return [now.subtract(1, 'week').startOf('week'), now.subtract(1, 'week').endOf('week')];
      case 'this-month':
        return [now.startOf('month'), now.endOf('month')];
      case 'last-month':
        return [now.subtract(1, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')];
      case 'this-quarter':
        return [now.startOf('quarter'), now.endOf('quarter')];
      case 'last-quarter':
        return [now.subtract(1, 'quarter').startOf('quarter'), now.subtract(1, 'quarter').endOf('quarter')];
      case 'this-year':
        return [now.startOf('year'), now.endOf('year')];
      case 'last-year':
        return [now.subtract(1, 'year').startOf('year'), now.subtract(1, 'year').endOf('year')];
      default:
        return [now.startOf('month'), now.endOf('month')];
    }
  };

  const handlePeriodChange = (value: string) => {
    setSelectedPeriod(value);
    const period = periodOptions.find(p => p.value === value) || periodOptions[2];
    setGroupBy(period.groupBy);
    if (value !== 'custom') {
      const [start, end] = calculateDateRange(value);
      setPickerRange([start, end]);
      setDateRange({
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
      });
      setIsCustomDate(false);
    } else {
      setIsCustomDate(true);
    }
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates) {
      setPickerRange([dates[0], dates[1]]);
      setDateRange({
        startDate: dates[0].format('YYYY-MM-DD'),
        endDate: dates[1].format('YYYY-MM-DD'),
      });
    }
  };

  // Function to build query parameters for API calls
  const buildParamsObject = (
    dateRange?: { startDate: string, endDate: string } | null,
    groupBy?: 'day' | 'month' | 'week' | null
  ) => {
    const params: Record<string, string> = {};

    if (selectedUser !== null && selectedUser !== undefined && selectedUser !== '') {
      params.user_id = String(selectedUser);
    }

    if (dateRange?.startDate) {
      params.start_date = dateRange.startDate;
    }

    if (dateRange?.endDate) {
      params.end_date = dateRange.endDate;
    }

    if (groupBy) {
      params.group_by = groupBy;
    }

    return params;
  };

  const buildParams = (
    dateRange?: { startDate: string, endDate: string } | null,
    groupBy?: 'day' | 'month' | 'week' | null
  ) => {
    const searchParams = new URLSearchParams(buildParamsObject(dateRange, groupBy));
    const paramString = searchParams.toString();
    return paramString ? `?${paramString}` : '';
  };

  // Helper function to create headers
  const getAuthHeaders = () => {
    const jwt = getAccessTokenOrRedirect(Cookies.get.bind(Cookies));
    return {
      'Authorization': `Bearer ${jwt}`
    };
  };

  // Efforts section data - updates when selected user or date range changes
  useEffect(() => {
    if (!userInfo) return;
    let mounted = true;

    // Set loading states
    setCallsLoading(true);
    setEmailsLoading(true);
    setContactCompanyStatsLoading(true);

    // Reset error states
    setCallsError(null);
    setEmailsError(null);
    setContactCompanyStatsError(null);

    const fetchEffortsData = async () => {
      try {
        const headers = getAuthHeaders();
        const params = buildParams(dateRange, groupBy);

        await Promise.all([
          // Fetch calls data
          (async () => {
            try {
              const callsResponse = await axios.get(`${API_URL}/api/calls/${params}`, { headers });
              if (mounted) {
                setCallsData(callsResponse.data.calls);
              }
            } catch (err) {
              if (mounted) {
                setCallsError(err instanceof Error ? err.message : 'Failed to fetch calls data');
                setCallsData([]);
              }
            } finally {
              if (mounted) {
                setCallsLoading(false);
              }
            }
          })(),

          // Fetch emails data
          (async () => {
            try {
              const emailsResponse = await axios.get(`${API_URL}/api/emails/${params}`, { headers });
              if (mounted) {
                setEmailsData(emailsResponse.data.emails);
              }
            } catch (err) {
              if (mounted) {
                setEmailsError(err instanceof Error ? err.message : 'Failed to fetch emails data');
                setEmailsData([]);
              }
            } finally {
              if (mounted) {
                setEmailsLoading(false);
              }
            }
          })(),

          // Fetch contact company count stats
          (async () => {
            try {
              const contactStatsResponse = await axios.get(`${API_URL}/api/contact-companies-stats/${params}`, { headers });
              if (mounted) {
                setContactCompanyStats(contactStatsResponse.data || {});
              }
            } catch (err: any) {
              if (mounted) {
                // Handle the specific error format {'error': str(e)}
                let errorMessage = 'Failed to fetch contact company stats';
                if (err.response && err.response.data && err.response.data.error) {
                  errorMessage = err.response.data.error;
                } else if (err instanceof Error) {
                  errorMessage = err.message;
                }
                setContactCompanyStatsError(errorMessage);
                setContactCompanyStats({});
              }
            } finally {
              if (mounted) {
                setContactCompanyStatsLoading(false);
              }
            }
          })()
        ]);

      } catch (err) {
        const error = err instanceof Error ? err.message : 'Failed to fetch data';
        if (mounted) {
          setCallsError(error);
          setEmailsError(error);
          setCallsLoading(false);
          setEmailsLoading(false);
        }
      }
    };

    fetchEffortsData();

    return () => {
      mounted = false;
    };
  }, [userInfo, selectedUser, dateRange, groupBy]); // Run when userInfo, selectedUser, or dateRange changes

  // Results section data - updates when selectedUser or dateRange changes
  useEffect(() => {
    if (!userInfo) return;
    let mounted = true;

    // Set loading states
    setSalesLoading(true);
    setSalesOrderDataLoading(true);
    setRevenueLoading(true);
    setQuotationsLoading(true);
    setEnquiriesLoading(true);
    setOpportunitiesLoading(true);

    // Reset error states
    setSalesError(null);
    setSalesOrderDataError(null);
    setRevenueError(null);
    setQuotationsError(null);
    setEnquiriesError(null);
    setOpportunitiesError(null);

    const fetchResultsData = async () => {
      try {
        const headers = getAuthHeaders();
        const params = buildParams(dateRange);
        const salesParamsObject = buildParamsObject(dateRange, groupBy);

        // Fetch all Results data in parallel
        await Promise.all([
          // Fetch sales data (for charts and monthly data)
          (async () => {
            try {
              const salesResponse = await axios.get(`${API_URL}/api/sales/`, {
                headers,
                params: salesParamsObject,
              });
              if (mounted) {
                const responseData = salesResponse.data || {};
                const rawPerformance = Array.isArray(responseData.sales)
                  ? responseData.sales
                  : Array.isArray(responseData.data)
                  ? responseData.data
                  : Array.isArray(responseData.performance_data)
                  ? responseData.performance_data
                  : [];

                const performanceData = rawPerformance
                  .map((item: any): SalesPerformanceDataPoint | null => {
                    if (!item) return null;
                    const parsedDate = dayjs(item.date);
                    if (!parsedDate.isValid()) return null;

                    const totalOrders = Number(item.total_orders ?? item.total ?? 0);
                    const ordersAmount = Number(
                      item.orders_amount ??
                      item.total_sales ??
                      item.value ??
                      0
                    );
                    const totalInvoices = Number(item.total_invoices ?? 0);
                    const invoicesAmount = Number(item.invoices_amount ?? 0);

                    return {
                      date: parsedDate.format('YYYY-MM-DD'),
                      total_orders: Number.isFinite(totalOrders) ? totalOrders : 0,
                      orders_amount: Number.isFinite(ordersAmount) ? ordersAmount : 0,
                      total_invoices: Number.isFinite(totalInvoices) ? totalInvoices : 0,
                      invoices_amount: Number.isFinite(invoicesAmount) ? invoicesAmount : 0,
                    };
                  })
                  .filter(
                    (item: SalesPerformanceDataPoint | null): item is SalesPerformanceDataPoint =>
                      item !== null
                  )
                  .sort((a: any, b: any) => dayjs(a.date).diff(dayjs(b.date)));

                const totalOrders = typeof responseData.total_orders === 'number'
                  ? responseData.total_orders
                  : performanceData.reduce((sum: number, item: any) => sum + (item?.total_orders || 0), 0);
                const totalOrdersAmount = typeof responseData.orders_amount === 'number'
                  ? responseData.orders_amount
                  : performanceData.reduce((sum: number, item: any) => sum + (item?.orders_amount || 0), 0);

                setSalesData({
                  total_sales: Number.isFinite(totalOrdersAmount) ? Number(totalOrdersAmount) : 0,
                  total_orders: Number.isFinite(totalOrders) ? Number(totalOrders) : 0,
                  monthly_data: Array.isArray(responseData.monthly_data) ? responseData.monthly_data : [],
                  daily_data: Array.isArray(responseData.daily_data) ? responseData.daily_data : [],
                  performance_data: performanceData,
                });
              }
            } catch (err) {
              if (mounted) {
                setSalesError(err instanceof Error ? err.message : 'Failed to fetch sales data');
                setSalesData({
                  total_sales: 0,
                  total_orders: 0,
                  monthly_data: [],
                  daily_data: [],
                  performance_data: [],
                });
              }
            } finally {
              if (mounted) {
                setSalesLoading(false);
              }
            }
          })(),

          // Fetch sales order data specifically for current period
          (async () => {
            try {
              const salesOrderDataResponse = await axios.get(`${API_URL}/api/sales-data/${params}`, { headers });
              if (mounted) {
                setSalesOrderData(salesOrderDataResponse.data || {
                  total_sales: 0,
                  total_orders: 0
                });
              }
            } catch (err) {
              if (mounted) {
                setSalesOrderDataError(err instanceof Error ? err.message : 'Failed to fetch sales order data');
              }
            } finally {
              if (mounted) {
                setSalesOrderDataLoading(false);
              }
            }
          })(),

          // Fetch revenue data
          (async () => {
            try {
              const revenueResponse = await axios.get(`${API_URL}/api/revenue/${params}`, { headers });
              if (mounted) {
                setRevenueData(revenueResponse.data || {
                  total_revenue: 0,
                  total_invoices: 0
                });
              }
            } catch (err) {
              if (mounted) {
                setRevenueError(err instanceof Error ? err.message : 'Failed to fetch revenue data');
              }
            } finally {
              if (mounted) {
                setRevenueLoading(false);
              }
            }
          })(),

          // Fetch quotations data
          (async () => {
            try {
              const quotationsResponse = await axios.get(`${API_URL}/api/quotations/${params}`, { headers });
              if (mounted) {
                setQuotationsData(quotationsResponse.data || {
                  total_quotations: 0,
                  total_amount: 0
                });
              }
            } catch (err) {
              if (mounted) {
                setQuotationsError(err instanceof Error ? err.message : 'Failed to fetch quotations data');
              }
            } finally {
              if (mounted) {
                setQuotationsLoading(false);
              }
            }
          })(),

          // Fetch enquiries data
          (async () => {
            try {
              const enquiriesResponse = await axios.get(`${API_URL}/api/enquiries/${params}`, { headers });
              if (mounted) {
                setEnquiriesData(enquiriesResponse.data || {
                  total_enquiries: 0,
                  total_items: 0
                });
              }
            } catch (err) {
              if (mounted) {
                setEnquiriesError(err instanceof Error ? err.message : 'Failed to fetch enquiries data');
              }
            } finally {
              if (mounted) {
                setEnquiriesLoading(false);
              }
            }
          })(),

          // Fetch opportunities data
          (async () => {
            try {
              const opportunitiesResponse = await axios.get(`${API_URL}/api/opportunities/${params}`, { headers });
              if (mounted) {
                setOpportunitiesData(opportunitiesResponse.data || {
                  total_opportunities: 0,
                  total_items: 0
                });
              }
            } catch (err) {
              if (mounted) {
                setOpportunitiesError(err instanceof Error ? err.message : 'Failed to fetch opportunities data');
              }
            } finally {
              if (mounted) {
                setOpportunitiesLoading(false);
              }
            }
          })(),

          // Fetch Financial Performance
          (async () => {
            try {
              // Always pass user_id when not admin; also pass when a user is filtered
              const fpParams = new URLSearchParams();
              if (selectedUser) {
                fpParams.append('user_id', selectedUser.toString());
              } else if (userInfo && !userInfo.isAdmin && userInfo.userId) {
                fpParams.append('user_id', userInfo.userId.toString());
              }
              const finaceResponse = await axios.get(
                `${API_URL}/api/financial-performance/${fpParams.toString() ? `?${fpParams.toString()}` : ''}`,
                { headers }
              );
              if (mounted) {
                const formatData: any = [{ label: "SO Raised Nos" }, { label: "SO Raised in Values" }, { label: "No of SO Invoices" }, { label: "SO Invoice Values" }];
                const rawData = finaceResponse?.data?.result || {};
                Object.keys(rawData).forEach((key) => {
                  formatData.forEach((item: any) => {
                    item[`${key}_so_count`] = rawData[key].so_count || 0;
                    item[`${key}_percent_change_so_count`] = rawData[key].percent_change_so_count || 0;
                    item[`${key}_so_value`] = rawData[key].so_value || 0;
                    item[`${key}_percent_change_so_value`] = rawData[key].percent_change_so_value || 0;
                    item[`${key}_so_invoice_count`] = rawData[key].so_invoice_count || 0;
                    item[`${key}_percent_change_so_invoice_count`] = rawData[key].percent_change_so_invoice_count || 0;
                    item[`${key}_sales_total`] = rawData[key].sales_total || 0;
                    item[`${key}_percent_change_sales_total`] = rawData[key].percent_change_sales_total || 0;
                  })
                })

                console.log("finace format data ", formatData)
                setFinacePermanceData(formatData);

              }
            } catch (err) {
              if (mounted) {
                setFinacePerformanceError(err instanceof Error ? err.message : 'Failed to fetch opportunities data');
              }
            } finally {
              if (mounted) {
                setFinacePerformanceLoading(false);
              }
            }
          })()
        ]);
      } catch (err) {
        const error = err instanceof Error ? err.message : 'Failed to fetch data';
        if (mounted) {
          setSalesError(error);
          setSalesOrderDataError(error);
          setRevenueError(error);
          setQuotationsError(error);
          setEnquiriesError(error);
          setOpportunitiesError(error);

          setSalesLoading(false);
          setSalesOrderDataLoading(false);
          setRevenueLoading(false);
          setQuotationsLoading(false);
          setEnquiriesLoading(false);
          setOpportunitiesLoading(false);
        }
      }
    };

    fetchResultsData();

    return () => {
      mounted = false;
    };
  }, [userInfo, selectedUser, dateRange, groupBy]); // Run when userInfo, selectedUser, dateRange, or grouping changes

  return (
    <>
      <div className="p-4 space-y-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className='text-left'>
            <h1 className="text-2xl font-semibold text-slate-900">
              Sales Performance - Q{Math.floor((new Date().getMonth() / 3) + 1)}
            </h1>
            <p className="text-slate-500">Track your sales activities and results</p>
          </div>
          <div className="w-full md:w-auto flex flex-col md:flex-row md:items-center justify-end gap-4">
            <div className="flex w-full flex-col sm:flex-row sm:items-center gap-3 md:gap-4">
              <Select
                value={selectedPeriod}
                onChange={handlePeriodChange}
                className="w-full sm:w-48 md:w-56"
                style={{ width: '100%', maxWidth: '14rem' }}
                options={periodOptions}
              />
              {isCustomDate && (
                <RangePicker
                  value={pickerRange}
                  onChange={handleDateRangeChange}
                  className="w-full sm:w-64"
                  style={{ width: '100%', maxWidth: '18rem' }}
                />
              )}
            </div>
            {(userInfo?.isAdmin || userInfo?.isTeamLead || (fullHierachyAccess && fullHierachyAccess !== 'loading')) && (
              <UserDropdown
                onUserChange={setSelectedUser}
                className="text-left w-full md:w-auto"
                isAdmin={userInfo?.isAdmin || false}
                isTeamLead={userInfo?.isTeamLead || false}
                currentUserId={userInfo?.userId}
              />
            )}
          </div>
        </div>

        {/* Inputs Section */}
        <div className="w-full">
          <InputsSection selectedUser={selectedUser} dateRange={dateRange} />
        </div>

        {/* Efforts Section */}
        <div className="w-full">
          <EffortsSection
            callsData={callsData}
            emailsData={emailsData}
            contactCompanyStats={contactCompanyStats}
            callsLoading={callsLoading}
            emailsLoading={emailsLoading}
            contactCompanyStatsLoading={contactCompanyStatsLoading}
            callsError={callsError}
            emailsError={emailsError}
            contactCompanyStatsError={contactCompanyStatsError}
            periodLabel={periodLabel}
            groupBy={groupBy}
            dateRange={dateRange}
            isUserFiltered={!!selectedUser}
          />
        </div>

        {/* Results Section */}
        <div className="w-full">
          <ResultsSection
            salesData={salesData}
            salesPerformanceData={salesData.performance_data ?? []}
            salesOrderAmount={salesOrderData.total_sales}
            revenue={revenueData.total_revenue}
            finacePermance={finacePermanceData}
            isLoading={
              salesLoading ||
              salesOrderDataLoading ||
              revenueLoading ||
              quotationsLoading ||
              enquiriesLoading ||
              opportunitiesLoading || 
              finacePerformanceLoading
            }
            error={
              salesError ||
              salesOrderDataError ||
              revenueError ||
              quotationsError ||
              enquiriesError ||
              opportunitiesError ||
              finacePerformanceError
            }
            pipelineData={createPipelineData(
              salesData,
              revenueData,
              quotationsData,
              enquiriesData,
              opportunitiesData
            )}
            periodLabel={periodLabel}
            groupBy={groupBy}
          />
        </div>

        {/* Rewards Preview (Only for specific users) */}
        {(!userInfo?.isAdmin || !(fullHierachyAccess && fullHierachyAccess !== 'loading') || selectedUser) && (
          <div className="w-full">
            <RewardsPreview isTeamLead={userInfo?.isTeamLead} selectedUser={selectedUser} />
          </div>
        )}
      </div>
    </>
  );
};

export default SalesDashboard;
